import { explorerUrl } from '@helpers/vivinoUrls';
import { Anchor, useI18N } from '@vivino/js-web-common';
import { RubyLibMerchant } from '@webtypes/rubyLibApi';
import cx from 'classnames';
import React, { Fragment } from 'react';
import { track } from 'vivino-js/analytics';
import { TrackingEvents } from 'vivino-js/helpers/splitHelper';
import MerchantIcon from 'vivino-ui/icons/Merchant';
import ThermometerIcon from 'vivino-ui/icons/Thermometer';

import { isLinkoutMerchant } from '@lib/merchants';

import TemperatureControlInformation from 'vivino-js/components/TemperatureControlInformation';

import styles from './merchantDetails.scss';

const TRANSLATIONS = {
  temperatureControl: 'components.deal_wine_page.merchant_details.temperature_control',
  info: 'components.deal_wine_page.merchant_details.info',
  soldBy: 'components.deal_wine_page.merchant_details.sold_by',
  termsAndConditions: 'components.deal_wine_page.merchant_details.terms_and_conditions',
  impressum: 'components.deal_wine_page.merchant_details.impressum',
  importedBy: 'components.deal_wine_page.merchant_details.imported_by',
  producedBy: 'components.deal_wine_page.merchant_details.produced_by',
  temperatureControlled: 'components.deal_wine_page.merchant_details.temperature_controlled',
  shipping: 'components.deal_wine_page.merchant_details.shipping',
};

interface InfoLinkProps {
  link: string;
  translation: string;
}

const InfoLink = ({ link, translation }: InfoLinkProps) => {
  const { t } = useI18N();
  if (!link) {
    return null;
  }

  return (
    <Anchor
      className={cx(styles.infoItem, styles.link)}
      href={link}
      target="_blank"
      rel="noopener noreferrer"
    >
      {t(translation)}
    </Anchor>
  );
};

interface InfoItemProps {
  address: string;
  translation: string;
}

const InfoItem = ({ address, translation }: InfoItemProps) => {
  const { t } = useI18N();

  if (!address) {
    return null;
  }

  return (
    <div className={styles.row}>
      <div className={styles.infoItem}>
        {t(translation)}: <br /> {address}
      </div>
    </div>
  );
};

interface MerchantDetailsPresentationalProps {
  className?: string;
  timeToNextTemperatureControlShipment?: string;
  merchant: RubyLibMerchant;
  showInfoSection?: boolean;
  shouldShowInfoSection?: boolean;
  handleInfoSectionDisplay: () => void;
  tosUrl?: string;
  impressumUrl?: string;
  producerAddress?: string;
  importerAddress?: string;
  handleTemperatureControlDisplay: () => void;
  showTemperatureControlToolTip?: boolean;
}

const MerchantDetailsPresentational = ({
  className,
  timeToNextTemperatureControlShipment,
  merchant,
  showInfoSection = false,
  shouldShowInfoSection = false,
  handleInfoSectionDisplay,
  tosUrl,
  impressumUrl,
  producerAddress,
  importerAddress,
  showTemperatureControlToolTip = false,
  handleTemperatureControlDisplay,
}: MerchantDetailsPresentationalProps) => {
  const { t } = useI18N();

  const handleMerchantLinkClicked = () => {
    track({
      event: TrackingEvents.MORE_MERCHANT_RECOMMENDATIONS_CLICK,
      props: {
        merchant_id: merchant.id,
      },
    });
  };

  const merchantNameLink = isLinkoutMerchant(merchant) ? (
    <Fragment key="merchantDetailsFragment">
      <span>{merchant.name}</span>
    </Fragment>
  ) : (
    <Fragment key="merchantDetailsFragment">
      <Anchor href={explorerUrl({ merchantId: merchant.id })} onClick={handleMerchantLinkClicked}>
        {merchant.name}
      </Anchor>
    </Fragment>
  );

  return (
    <div className={cx(styles.outer, className)}>
      {timeToNextTemperatureControlShipment && (
        <div className={styles.row} data-testid="temperatureControlInfo">
          <ThermometerIcon className={styles.icon} />

          <div onClick={handleTemperatureControlDisplay} className={styles.temperatureControl}>
            {t(TRANSLATIONS.temperatureControlled)}
            <TemperatureControlInformation
              show={showTemperatureControlToolTip}
              onClose={handleTemperatureControlDisplay}
            />
          </div>
          {`\u00A0${t(TRANSLATIONS.shipping)}`}
        </div>
      )}

      <div className={styles.row}>
        <span className={styles.icon}>
          <MerchantIcon />
        </span>
        <div>
          <span>{t(TRANSLATIONS.soldBy, { merchantName: merchantNameLink })}</span>
          {shouldShowInfoSection && (
            <span className={styles.info} onClick={handleInfoSectionDisplay}>
              {t(TRANSLATIONS.info)}
            </span>
          )}
        </div>
      </div>

      {showInfoSection && (
        <div className={styles.infoContainer}>
          <div className={styles.row}>
            <InfoLink link={tosUrl} translation={TRANSLATIONS.termsAndConditions} />
            <InfoLink link={impressumUrl} translation={TRANSLATIONS.impressum} />
          </div>

          <InfoItem address={importerAddress} translation={TRANSLATIONS.importedBy} />
          <InfoItem address={producerAddress} translation={TRANSLATIONS.producedBy} />
        </div>
      )}
    </div>
  );
};

export default MerchantDetailsPresentational;
